import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ConfirmDialog, Drawer, FormField } from "@outpave/ds";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { useFormik } from "formik";
import { useMutation, useQuery } from "@apollo/client";
import {
  GET_BALANCE_SETTINGS,
  UPDATE_COMPANY_BALANCE_SETTING,
  REMOVE_COMPANY_BALANCE_SETTING,
  ENABLE_COMPANY_BALANCE_SETTING,
} from "../../../graphql/company";
import { toast } from "react-toastify";
import maintainBalance from "../../../validations/maintainBalance";
import CurrencyInput from "../common/CurrencyInput";
import { useFormikHelpers } from "hooks/useFormikHelpers";
import { BankAccount } from "hooks/useGetBankAccounts";
import { Inline, Stack } from "components/layout/reusableLayouts";
import {
  AccountSelect,
  ConfirmTransfer,
  MemoField,
  TransferAccountsRow,
} from "./TransferFormFields";
import AutoBalanceAuthorizationDialog from "./AutoBalanceAuthorizationDialog";

interface FormikMaintainBalanceProps {
  minimumBalance: number;
  targetBalance: number;
  memo: string;
  confirmTransfer: boolean;
}

interface AutoBalanceDrawerProps {
  open: boolean;
  fromAccountsData: BankAccount[];
  toAccountsData: BankAccount[];
  handleCloseDrawer: (isReloadData: boolean) => void;
  refetchAccounts: () => void;
}

const AutoBalanceDrawer: React.FC<AutoBalanceDrawerProps> = (props) => {
  const {
    open,
    fromAccountsData,
    toAccountsData,
    handleCloseDrawer,
    refetchAccounts,
  } = props;

  const { t } = useTranslation();

  const {
    data: balanceSettings,
    loading: loadingBalance,
    refetch: refetchBalanceSettings,
  } = useQuery(GET_BALANCE_SETTINGS, {
    fetchPolicy: "network-only",
  });

  const [maintainBalanceFunct, { loading: loadingMaintainBalance }] =
    useMutation(UPDATE_COMPANY_BALANCE_SETTING, {
      onCompleted: () => {
        formik.resetForm();
        setEditingBalanceSettings(false);
        handleCloseDrawer(true);
        toast.success(t("Balance settings successfully updated"));
        refetchBalanceSettings();
      },
      onError: (err) => {
        toast.error(t(err.message));
      },
    });

  const [
    removeCompanyBalanceSetting,
    { loading: loadingRemoveCompanyBalanceSetting },
  ] = useMutation(REMOVE_COMPANY_BALANCE_SETTING, {
    onCompleted: () => {
      formik.resetForm();
      setEditingBalanceSettings(false);
      setConfirmRemove(false);
      handleCloseDrawer(true);
      toast.success(t("Auto Balance successfully removed"));
      refetchBalanceSettings();
    },
    onError: (err) => {
      toast.error(t(err.message));
    },
  });

  const [
    enableCompanyBalanceSetting,
    { loading: loadingEnableCompanyBalanceSetting },
  ] = useMutation(ENABLE_COMPANY_BALANCE_SETTING, {
    onCompleted: () => {
      toast.success(t("autoTransferRulesEnabled"));
      refetchBalanceSettings();
    },
    onError: (err) => {
      toast.error(t(err.message));
    },
  });

  const [fromAccount, setFromAccount] = useState("");
  const [toAccount, setToAccount] = useState("");
  const companyBalanceSettings =
    balanceSettings?.getBalanceSettings?.TARGET_BALANCE;

  const hasBalanceSettings =
    !!balanceSettings?.getBalanceSettings?.TARGET_BALANCE?.toAccountId;
  const isBalanceSettingDisabled =
    hasBalanceSettings && Boolean(companyBalanceSettings?.isDisabled);

  const [editingBalanceSettings, setEditingBalanceSettings] =
    useState<boolean>(false);
  const [showAuthorization, setShowAuthorization] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);

  useEffect(() => {
    if (open) {
      setEditingBalanceSettings(!hasBalanceSettings);
    }
  }, [open, hasBalanceSettings]);

  const handleEditBalance = () => {
    formik.setFieldValue("confirmTransfer", false);
    setEditingBalanceSettings(!editingBalanceSettings);
  };

  const formik = useFormik<FormikMaintainBalanceProps>({
    initialValues: {
      minimumBalance: 2000.0,
      targetBalance: 5000.0,
      memo: "",
      confirmTransfer: false,
    },
    validationSchema: maintainBalance,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const { minimumBalance, targetBalance, memo } = values;
      if (!fromAccount) {
        toast.error(t("Please select a funding account"));
        return;
      }
      const input = {
        minimumBalance,
        targetBalance,
        memo,
        fromAccountId: fromAccount,
        toAccountId: toAccount,
      };
      await maintainBalanceFunct({
        variables: {
          input,
        },
      });
    },
  });

  const { handleFormikHelperText, handleFormikError } =
    useFormikHelpers(formik);

  useEffect(() => {
    if (hasBalanceSettings) {
      formik.setValues({
        minimumBalance: companyBalanceSettings.minimumBalance,
        targetBalance: companyBalanceSettings.targetBalance,
        memo: companyBalanceSettings.memo || "",
        confirmTransfer: false,
      });
    } else {
      formik.setValues(formik.initialValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyBalanceSettings, open, editingBalanceSettings]);

  useEffect(() => {
    formik.validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!open) return;

    if (
      hasBalanceSettings &&
      companyBalanceSettings?.fromAccountId &&
      companyBalanceSettings?.toAccountId
    ) {
      setFromAccount(companyBalanceSettings.fromAccountId);
      setToAccount(companyBalanceSettings.toAccountId);
    } else if (
      fromAccountsData?.length > 0 &&
      toAccountsData?.length > 0 &&
      !hasBalanceSettings
    ) {
      setFromAccount(fromAccountsData[0].id);
      setToAccount(toAccountsData[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    hasBalanceSettings,
    companyBalanceSettings?.fromAccountId,
    fromAccountsData,
  ]);

  // The save gates dismissal; the balance fetch only veils, so a hanging
  // query cannot trap the user. The legacy header X skipped the
  // setEditingBalanceSettings reset the backdrop did, one path now.
  const busy = loadingMaintainBalance;

  const handleClose = () => {
    if (busy) return;
    formik.resetForm();
    setEditingBalanceSettings(false);
    setShowAuthorization(false);
    setConfirmRemove(false);
    handleCloseDrawer(false);
  };

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      title={t("Auto Balance")}
      width="544px"
      loading={loadingBalance || busy}
      footer={
        editingBalanceSettings ? (
          <Button
            variant="primary-filled"
            // No `isValid` gate: `handleSubmit` validates, touches every
            // field so the errors paint, and refuses to submit.
            disabled={!formik.values.confirmTransfer || loadingMaintainBalance}
            onClick={() => formik.handleSubmit()}
          >
            {loadingMaintainBalance
              ? t("Saving")
              : hasBalanceSettings
                ? t("Maintain")
                : t("Set up Auto Balance")}
          </Button>
        ) : undefined
      }
    >
      <form onSubmit={formik.handleSubmit}>
        <Stack gap="var(--space-6)">
          <TransferAccountsRow
            from={
              <AccountSelect
                name="fromAccount"
                ariaLabel={t("From")}
                value={fromAccount}
                accounts={fromAccountsData}
                onSelect={setFromAccount}
                onAccountAdded={setFromAccount}
                refetchAccounts={refetchAccounts}
              />
            }
            to={
              <AccountSelect
                name="toAccount"
                ariaLabel={t("To")}
                value={toAccount}
                accounts={toAccountsData}
                onSelect={setToAccount}
              />
            }
          />

          <Inline justify="between" wrap={false} sx={{ width: "100%" }}>
            <Typography variant="subtitle1" component="h3">
              {!hasBalanceSettings
                ? t("Set up Auto Balance")
                : isBalanceSettingDisabled
                  ? t("Auto Balance is off")
                  : t("Auto Balance is enabled")}
            </Typography>

            {hasBalanceSettings &&
              (editingBalanceSettings ? (
                <Button
                  size="small"
                  variant="default-outlined"
                  onClick={handleEditBalance}
                >
                  {t("Cancel")}
                </Button>
              ) : (
                <Inline gap="var(--space-2)" wrap={false}>
                  <Button
                    size="small"
                    variant="default-outlined"
                    onClick={handleEditBalance}
                  >
                    {t("Edit")}
                  </Button>
                  <Button
                    size="small"
                    variant="danger-outlined"
                    onClick={() => setConfirmRemove(true)}
                  >
                    {t("Remove")}
                  </Button>
                </Inline>
              ))}
          </Inline>

          {isBalanceSettingDisabled && !editingBalanceSettings && (
            <Stack gap="var(--space-3)" sx={{ alignItems: "flex-start" }}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {t("AutoBalanceOffDescription")}
              </Typography>
              <Button
                size="small"
                variant="primary-filled"
                disabled={loadingEnableCompanyBalanceSetting}
                onClick={() => enableCompanyBalanceSetting()}
              >
                {t("reEnableAutoTransfer")}
              </Button>
            </Stack>
          )}

          <Stack gap="var(--space-3)">
            <FormField
              label={t("Target Balance")}
              htmlFor="targetBalance"
              required
              disabled={!editingBalanceSettings}
              sx={{ maxWidth: 320 }}
            >
              <CurrencyInput
                id="targetBalance"
                name="targetBalance"
                value={formik.values.targetBalance}
                fullWidth
                size="md"
                required
                onChange={(raw) => formik.setFieldValue("targetBalance", raw)}
                onBlur={formik.handleBlur}
                error={handleFormikError("targetBalance")}
                helperText={handleFormikHelperText("targetBalance")}
                disabled={!editingBalanceSettings}
              />
            </FormField>
          </Stack>

          <FormField
            label={t("Minimum Balance")}
            htmlFor="minimumBalance"
            required
            disabled={!editingBalanceSettings}
            sx={{ maxWidth: 320 }}
          >
            <CurrencyInput
              id="minimumBalance"
              name="minimumBalance"
              value={formik.values.minimumBalance}
              fullWidth
              size="md"
              required
              onChange={(raw) => formik.setFieldValue("minimumBalance", raw)}
              onBlur={formik.handleBlur}
              error={handleFormikError("minimumBalance")}
              helperText={handleFormikHelperText("minimumBalance")}
              disabled={!editingBalanceSettings}
            />
          </FormField>

          <Typography variant="body2">
            {t("MaintainBalanceDescription")}
          </Typography>

          <MemoField
            value={formik.values.memo}
            onChange={(memo) => formik.setFieldValue("memo", memo)}
            label={t("Auto Balance Memo")}
            placeholder={t("Add a note for this transfer") as string}
            disabled={!editingBalanceSettings}
          />

          <ConfirmTransfer
            checked={formik.values.confirmTransfer}
            // Ticking asks for the agreement rather than setting the field:
            // the box IS the record that the authorization was accepted, so
            // only accepting it may tick it.
            onChange={(checked) => {
              if (checked) {
                setShowAuthorization(true);
                return;
              }
              formik.setFieldValue("confirmTransfer", false);
            }}
            disabled={!editingBalanceSettings}
            label={t("AchDebitAuthorization")}
          />
        </Stack>
      </form>

      <ConfirmDialog
        open={confirmRemove}
        title={t("Remove Auto Balance")}
        message={t("RemoveAutoBalanceConfirm")}
        confirmLabel={t("Remove")}
        cancelLabel={t("Cancel")}
        busy={loadingRemoveCompanyBalanceSetting}
        onConfirm={() => removeCompanyBalanceSetting()}
        onCancel={() => setConfirmRemove(false)}
      />

      <AutoBalanceAuthorizationDialog
        open={showAuthorization}
        onAccept={() => {
          setShowAuthorization(false);
          formik.setFieldValue("confirmTransfer", true);
        }}
        onClose={() => setShowAuthorization(false)}
      />
    </Drawer>
  );
};

export default AutoBalanceDrawer;
